import React from 'react';
import type { WidgetStrings } from '../utils/i18n';
import { DEFAULT_WIDGET_STRINGS, formatWidgetString } from '../utils/i18n';

interface TelemetryPanelProps {
  data: Record<string, unknown> | null;
  loading: boolean;
  error: string | null;
  showContext?: boolean;
  onRefresh?: () => void;
  strings?: WidgetStrings;
}

function formatNumber(value?: number | null) {
  if (value === undefined || value === null || Number.isNaN(value)) return '-';
  return Number(value).toLocaleString();
}

function formatMs(value?: number | null) {
  if (value === undefined || value === null || Number.isNaN(value)) return '-';
  return `${Math.round(value)} ms`;
}

export default function TelemetryPanel({
  data,
  loading,
  error,
  showContext = true,
  onRefresh,
  strings = DEFAULT_WIDGET_STRINGS,
}: TelemetryPanelProps) {
  const telemetry = data?.telemetry as Record<string, any> | undefined;
  const snapshot =
    telemetry && typeof telemetry === 'object' && 'requests' in telemetry ? telemetry : null;
  const last = snapshot?.last as Record<string, any> | undefined;
  const tokens = snapshot?.tokens as Record<string, any> | undefined;
  const context = snapshot?.context as Record<string, any> | undefined;

  return (
    <div className="rw-telemetry">
      <details>
        <summary>
          <span>{strings.devTelemetry}</span>
          {onRefresh && (
            <button type="button" className="rw-telemetry-refresh" onClick={onRefresh}>
              {strings.refresh}
            </button>
          )}
        </summary>
        <div className="rw-telemetry-body">
          {loading && <div className="rw-telemetry-muted">{strings.loadingTelemetry}</div>}
          {!loading && error && <div className="rw-telemetry-error">{error}</div>}
          {!loading && !error && !snapshot && (
            <div className="rw-telemetry-muted">{strings.noTelemetry}</div>
          )}
          {snapshot && (
            <>
              <div className="rw-telemetry-grid">
                <div className="rw-telemetry-card">
                  <div className="rw-telemetry-label">{strings.telemetryRequests}</div>
                  <div className="rw-telemetry-value">{formatNumber(snapshot.requests)}</div>
                </div>
                <div className="rw-telemetry-card">
                  <div className="rw-telemetry-label">{strings.telemetryErrors}</div>
                  <div className="rw-telemetry-value">{formatNumber(snapshot.errors)}</div>
                </div>
                <div className="rw-telemetry-card">
                  <div className="rw-telemetry-label">{strings.telemetryAvgLatency}</div>
                  <div className="rw-telemetry-value">{formatMs(snapshot.avgLatencyMs)}</div>
                </div>
                <div className="rw-telemetry-card">
                  <div className="rw-telemetry-label">{strings.telemetryLastLatency}</div>
                  <div className="rw-telemetry-value">{formatMs(snapshot.lastLatencyMs)}</div>
                </div>
                <div className="rw-telemetry-card">
                  <div className="rw-telemetry-label">{strings.telemetryKbHitRate}</div>
                  <div className="rw-telemetry-value">
                    {snapshot.requests
                      ? `${Math.round((snapshot.kbHits / snapshot.requests) * 100)}%`
                      : '-'}
                  </div>
                </div>
                <div className="rw-telemetry-card">
                  <div className="rw-telemetry-label">{strings.telemetryTokensAvg}</div>
                  <div className="rw-telemetry-value">
                    {tokens?.samples
                      ? formatNumber(Math.round((tokens.total || 0) / tokens.samples))
                      : '-'}
                    {tokens?.estimated ? <span className="rw-telemetry-pill">est.</span> : null}
                  </div>
                </div>
              </div>

              {last && (
                <div className="rw-telemetry-row">
                  <div className="rw-telemetry-muted">
                    {formatWidgetString(strings.telemetryLastRequest, {
                      provider: last.provider || 'provider',
                      model: last.model || 'model',
                      mode: last.stream ? strings.telemetryModeStream : strings.telemetryModeSync,
                    })}
                  </div>
                </div>
              )}

              {showContext && context && (
                <div className="rw-telemetry-context">
                  <div className="rw-telemetry-section">
                    <div className="rw-telemetry-heading">{strings.telemetryAttributes}</div>
                    {(context.attributes || []).length === 0 ? (
                      <div className="rw-telemetry-muted">{strings.none}</div>
                    ) : (
                      (context.attributes || []).map((attr: any, idx: number) => (
                        <div key={`attr-${idx}`} className="rw-telemetry-item">
                          <strong>{attr.name}</strong>
                          {attr.value !== null && attr.value !== undefined && attr.value !== ''
                            ? `: ${attr.value}`
                            : ''}
                          {attr.description ? ` - ${attr.description}` : ''}
                        </div>
                      ))
                    )}
                  </div>
                  <div className="rw-telemetry-section">
                    <div className="rw-telemetry-heading">{strings.telemetryRules}</div>
                    {(context.rules || []).length === 0 ? (
                      <div className="rw-telemetry-muted">{strings.none}</div>
                    ) : (
                      (context.rules || []).map((rule: any, idx: number) => (
                        <div key={`rule-${idx}`} className="rw-telemetry-item">
                          <strong>{rule.name}</strong>
                          {rule.description ? ` - ${rule.description}` : ''}
                        </div>
                      ))
                    )}
                  </div>
                  <div className="rw-telemetry-section">
                    <div className="rw-telemetry-heading">{strings.telemetryExamples}</div>
                    {(context.examples || []).length === 0 ? (
                      <div className="rw-telemetry-muted">{strings.none}</div>
                    ) : (
                      (context.examples || []).map((example: any, idx: number) => {
                        const messages = Array.isArray(example.messages) ? example.messages : [];
                        return (
                          <div key={`example-${idx}`} className="rw-telemetry-item">
                            <strong>{example.name}</strong>
                            {example.description ? ` - ${example.description}` : ''}
                            {messages.map((msg: any, msgIdx: number) => (
                              <div
                                key={`example-${idx}-msg-${msgIdx}`}
                                className="rw-telemetry-subitem"
                              >
                                {msg.customer ? `Customer: ${msg.customer}` : ''}
                                {msg.agent ? `${msg.customer ? ' | ' : ''}Agent: ${msg.agent}` : ''}
                              </div>
                            ))}
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div className="rw-telemetry-section">
                    <div className="rw-telemetry-heading">{strings.telemetryKbDocs}</div>
                    {(context.kbDocs || []).length === 0 ? (
                      <div className="rw-telemetry-muted">{strings.none}</div>
                    ) : (
                      (context.kbDocs || []).map((doc: any, idx: number) => (
                        <div key={`doc-${idx}`} className="rw-telemetry-item">
                          <strong>{doc.title}</strong>
                          {doc.score !== null && doc.score !== undefined
                            ? ` (${doc.score.toFixed(2)})`
                            : ''}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </details>
    </div>
  );
}
